<template>
  <v-row justify="start">
    <!-- 
      Jeden wiersz składa się z nazwy property i jego wartości.
      Otwarte wartości - można w pisać wartość z ręki
      Zamknięte wartości - wybór z listy zdefiniowanych wcześniej opcji
    -->
    <v-col cols="12" md="6" class="d-flex align-center ga-2">
      <!-- wybór jednego property z listy -->
      <v-select
        itemref="item"
        v-model="selectedPropertyType"
        :label="$t('admin-resources.filter.property.row.chooseProperty')"
        :items="allPropertiesDictionary"
        :item-title="item => propertyDescription(item)"
        item-value="code"
        @update:model-value="onSelectedSearchPropertyChange"
        return-object
        :rules="[
          v => !!v || $t('admin-resources.edit.properties.validation.propertyRequired'),
          v => !v || (props.usedCodes ?? []).filter(c => c === v.code).length <= 1 || $t('admin-resources.edit.properties.validation.propertyDuplicate'),
        ]"
        hide-details="auto"
        density="compact"
      ></v-select>
      <v-icon
        v-if="selectedPropertyType?.description"
        size="small"
        color="grey"
        v-tooltip="selectedPropertyType.description"
      >mdi-information-outline</v-icon>
    </v-col>

    <v-col cols="12" md="5" class="d-flex align-center ga-2">
      <v-combobox
        v-model="selectedPropertyOptions"
        :label="$t('admin-resources.filter.property.row.chooseOptionLabel')"
        :items="selectedPropertyAllPossibleOptions"
        item-title="defaultName"
        item-value="code"
        :loading="isLoading"
        v-model:search="search"
        multiple
        :placeholder="$t('admin-resources.filter.property.row.startTypingSearch')"
        :hide-no-data="!search"
        :rules="[v => !selectedPropertyType || v.length > 0 || $t('admin-resources.edit.properties.validation.valueRequired')]"
        hide-details="auto"
        density="compact"
        autocomplete="off"
      >
        <template v-slot:no-data>
          <v-list-item>
            <span class="subheading">{{ $t('admin-resources.filter.property.row.addCustomOption') }}</span>
            <span class="ml-1 font-weight-medium">{{ search }}</span>
          </v-list-item>
        </template>
        <template v-slot:selection="{ item, index }">
          <span>{{ item.raw.defaultName }}{{ index < selectedPropertyOptions.length - 1 ? ',\u00a0' : '' }}</span>
        </template>
      </v-combobox>
    </v-col>

    <v-col cols="12" md="1" class="d-flex align-center">
      <v-btn icon variant="text" @click="onRemovePropertyFilter">
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import type {
  ResourceDictPropertyOptionValue,
  ResourceDictPropertyValue,
  ResourceFilterPropertyValue,
} from '@/venit/services/model/techpubs-cms';
import ResourceManagementService from './resource-management.service';
import StringUtils from '@/shared/utils/string-utils.service';

const props = defineProps<{
  allPropertiesDictionary: ResourceDictPropertyValue[];
  resourceProperty: ResourceFilterPropertyValue;
  usedCodes?: string[];
}>();

const emit = defineEmits<{
  filterRowRemoved: [filterProperty: ResourceFilterPropertyValue];
}>();

const resourcesManagementService = inject<() => ResourceManagementService>('resourceService')!;
const { locale } = useI18n();

const selectedPropertyType = ref<ResourceDictPropertyValue | null>(null);
const selectedPropertyOptions = ref<ResourceDictPropertyOptionValue[]>([]);
const selectedPropertyAllPossibleOptions = ref<ResourceDictPropertyOptionValue[]>([]);
const selectedPropertyAllPossibleOptionsCodes = ref<string[]>([]);
const isLoading = ref(false);
const search = ref('');

const stringToValueOption = (val: string): ResourceDictPropertyOptionValue => {
  if (selectedPropertyType.value?.valueOpen) {
    return { code: val, defaultName: val, translations: [] };
  } else {
    return selectedPropertyType.value?.propertyOptions.find(opt => opt.code === val) as ResourceDictPropertyOptionValue;
  }
};

const recalculateOptions = () => {
  if (!props.resourceProperty || props.resourceProperty.propertyCode == null) {
    selectedPropertyType.value = null;
    selectedPropertyOptions.value = [];
    selectedPropertyAllPossibleOptions.value = [];
    return;
  }
  selectedPropertyType.value = props.allPropertiesDictionary.find(prop => prop.code === props.resourceProperty.propertyCode) || null;
  if (selectedPropertyType.value) {
    selectedPropertyAllPossibleOptions.value = selectedPropertyType.value.propertyOptions;
    selectedPropertyAllPossibleOptionsCodes.value = selectedPropertyType.value.propertyOptions.map(opt => opt.code);
    selectedPropertyOptions.value = props.resourceProperty.options.map(pv => stringToValueOption(pv));
  }
};

const updateFilterSelectedPropertyOptions = () => {
  props.resourceProperty.options = selectedPropertyOptions.value.filter(spo => !!spo).filter(spo => spo.code).map(spo => spo.code);
};

const onUpdatePropertyOptions = () => {
  if (!selectedPropertyType.value) return;
  if (!selectedPropertyType.value.valueOpen || !selectedPropertyType.value.code || StringUtils.isBlank(search.value)) return;
  isLoading.value = true;
  resourcesManagementService().listPropertyOptions(selectedPropertyType.value.code, search.value, 50).then(response => {
    selectedPropertyAllPossibleOptions.value = response.data;
    selectedPropertyAllPossibleOptionsCodes.value = response.data.map(opt => opt.code);
    isLoading.value = false;
  });
};

const onRemovePropertyFilter = () => emit('filterRowRemoved', props.resourceProperty);

const onSelectedSearchPropertyChange = () => {
  if (selectedPropertyType.value) props.resourceProperty.propertyCode = selectedPropertyType.value.code;
};

const propertyDescription = (property: ResourceDictPropertyValue) => {
  const translation = property.translations?.find(t => t.langCode === locale.value);
  return translation?.name || property.defaultName;
};


watch(selectedPropertyOptions, (val, prev) => {
  if (val.length === prev.length) return;
  selectedPropertyOptions.value = val.map(v => {
    if (typeof v === 'string') {
      const obj = stringToValueOption(v);
      selectedPropertyAllPossibleOptions.value.push(obj);
      return obj;
    }
    return v as ResourceDictPropertyOptionValue;
  });
  updateFilterSelectedPropertyOptions();
});

watch(search, onUpdatePropertyOptions);
watch(() => props.resourceProperty.propertyCode, () => recalculateOptions());
watch(() => props.allPropertiesDictionary, () => recalculateOptions());

recalculateOptions();
</script>